'use strict';

/**
 * MarkupStudio — three-panel editor for a single PDF markup session.
 *
 * URL:   /markup-studio/:sessionId
 *
 * Layout:
 *   ┌─────────────────────────────────────────────────────────────────────────────┐
 *   │ Title · re-render · versions · back                                         │
 *   ├──────────────┬───────────────────────────────────────────────┬──────────────┤
 *   │ MarkupList   │ PdfCanvas + MarkupOverlay (+ Thumbnails below)│ MarkupChat   │
 *   │  ↓ CRUD      │                                                │  (per markup │
 *   │              │                                                │   or whole)  │
 *   └──────────────┴───────────────────────────────────────────────┴──────────────┘
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, RefreshCw, History, ZoomIn, ZoomOut, Save } from 'lucide-react';
import toast from 'react-hot-toast';

import PdfCanvas from '../components/markupStudio/PdfCanvas';
import MarkupOverlay from '../components/markupStudio/MarkupOverlay';
import MarkupList from '../components/markupStudio/MarkupList';
import MarkupChat from '../components/markupStudio/MarkupChat';
import Thumbnails from '../components/markupStudio/Thumbnails';
import VersionHistory from '../components/markupStudio/VersionHistory';
import * as api from '../components/markupStudio/api';

const DEFAULT_NEW_BOX_SIZE_PDFPX = 100;   // dropped scope_box is this many PDF points square
const DEFAULT_NEW_CLOUD_SIZE_PDFPX = 80;  // dropped cloud diameter
const DEFAULT_NEW_CALLOUT_ARM_PDFPX = 80; // pdf points from anchor to label

export default function MarkupStudio() {
  const { sessionId } = useParams();
  const navigate = useNavigate();

  const [session, setSession] = useState(null);
  const [markups, setMarkups] = useState([]);     // live working copy
  const [pageNumber, setPageNumber] = useState(1);
  const [pageCount, setPageCount] = useState(0);
  // Two-axis zoom model:
  //   `scale` / `fitToWidth` control what the canvas is RASTERIZED at by
  //      PDF.js. We pin this to fit-to-width and don't change it on zoom
  //      so the canvas pixel dimensions stay constant — zooming is purely
  //      a CSS transform.
  //   `displayZoom` is the CSS-transform multiplier the user controls via
  //      +/- buttons. The canvas + SVG overlay scale together via GPU,
  //      and the surrounding scroll viewport stays the exact same layout
  //      size (panels don't reflow on zoom).
  // Bluebeam / Figma / Adobe all work this way.
  const [scale, setScale] = useState(1);
  const [fitToWidth, setFitToWidth] = useState(true);
  const [displayZoom, setDisplayZoom] = useState(1);
  // Camera position in CANVAS-PIXEL space (not screen pixels). At pan=(0,0)
  // the viewport shows the canvas's top-left corner. Updated by drag-pan;
  // reset on Fit. Bounded later by the PdfCanvas's effective size so the
  // camera can't fly off into empty space.
  const [panX, setPanX] = useState(0);
  const [panY, setPanY] = useState(0);
  const [selectedIdx, setSelectedIdx] = useState(-1);
  const scrollRef = useRef(null);
  const spaceHeldRef = useRef(false);

  // Track Space-key state for the hand-tool pan (Bluebeam convention:
  // hold space → left-drag pans, like Adobe / Figma / Photoshop). We use
  // a ref instead of state because read-during-mousedown should be sync
  // and we don't need to re-render on every key change.
  useEffect(() => {
    const onDown = (e) => {
      if (e.code === 'Space' && !isTypingInForm(e.target)) {
        spaceHeldRef.current = true;
      }
    };
    const onUp = (e) => {
      if (e.code === 'Space') spaceHeldRef.current = false;
    };
    const isTypingInForm = (el) => {
      if (!el) return false;
      const tag = el.tagName;
      return tag === 'INPUT' || tag === 'TEXTAREA' || el.isContentEditable;
    };
    window.addEventListener('keydown', onDown);
    window.addEventListener('keyup', onUp);
    return () => {
      window.removeEventListener('keydown', onDown);
      window.removeEventListener('keyup', onUp);
    };
  }, []);
  const [addMode, setAddMode] = useState(null);   // 'cloud' | 'callout' | 'scope_box' | null
  const [rendering, setRendering] = useState(false);
  const [showVersions, setShowVersions] = useState(false);
  const [error, setError] = useState(null);
  const [dirty, setDirty] = useState(false);
  const saveTimer = useRef(null);

  // Initial load
  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    api
      .getSession(sessionId)
      .then((data) => {
        if (cancelled) return;
        setSession(data);
        setMarkups(data.markups || []);
        if (data.pageCount) setPageCount(data.pageCount);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('[markup-studio] load failed:', err);
        setError(err?.response?.data?.error || err.message);
      });
    return () => { cancelled = true; };
  }, [sessionId]);

  // Debounced autosave on dirty
  useEffect(() => {
    if (!dirty || !sessionId) return undefined;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        await api.bulkSaveMarkups(sessionId, markups);
        setDirty(false);
      } catch (err) {
        console.warn('[markup-studio] autosave failed:', err);
        toast.error(`Save failed: ${err?.response?.data?.error || err.message}`);
      }
    }, 1200);
    return () => clearTimeout(saveTimer.current);
  }, [dirty, sessionId, markups]);

  // Per-page extraction strategy: we never load the full source PDF in the
  // browser. The /pdf-info endpoint gives us pageCount + dimensions, and
  // pdfUrl points at a server-extracted single-page micro-PDF for the page
  // we're currently viewing. Construction drawing sets routinely run 500
  // MB–1 GB; loading them in pdfjs OOMs the tab.
  const pdfUrl = useMemo(
    () => (sessionId ? api.pagePdfUrl(sessionId, pageNumber) : null),
    [sessionId, pageNumber],
  );

  useEffect(() => {
    if (!sessionId) return undefined;
    let cancelled = false;
    api
      .getPdfInfo(sessionId)
      .then((info) => {
        if (cancelled) return;
        if (info?.pageCount) setPageCount(info.pageCount);
      })
      .catch((err) => {
        console.warn('[markup-studio] pdf-info failed:', err?.message);
      });
    return () => { cancelled = true; };
  }, [sessionId]);

  // onReady from PdfCanvas reports pageCount: 1 (each request is a
  // single-page micro-PDF — authoritative count comes from /pdf-info), but
  // it also reports the effectiveScale that fit-to-width resolved to. We
  // capture that so the zoom controls have a meaningful starting value
  // when the user first clicks +/−.
  const onPdfReady = useCallback(({ scale: resolvedScale }) => {
    if (fitToWidth && resolvedScale) setScale(resolvedScale);
  }, [fitToWidth]);

  const onUpdateMarkup = useCallback((idx, patch) => {
    setMarkups((prev) => {
      const next = [...prev];
      if (!next[idx]) return prev;
      next[idx] = { ...next[idx], ...patch };
      return next;
    });
    setDirty(true);
  }, []);

  const onDeleteMarkup = useCallback((idx) => {
    setMarkups((prev) => prev.filter((_, i) => i !== idx));
    if (selectedIdx === idx) setSelectedIdx(-1);
    setDirty(true);
  }, [selectedIdx]);

  const onClickPdfCoords = useCallback(({ x, y }) => {
    if (!addMode) return;
    let nm;
    if (addMode === 'cloud') {
      const r = DEFAULT_NEW_CLOUD_SIZE_PDFPX / 2;
      nm = {
        type: 'cloud',
        page: pageNumber,
        risk: 'medium',
        label: 'New markup',
        coords: [
          [x - r, y - r],
          [x + r, y - r],
          [x + r, y + r],
          [x - r, y + r],
        ],
      };
    } else if (addMode === 'callout') {
      nm = {
        type: 'callout',
        page: pageNumber,
        risk: 'medium',
        label: 'New callout',
        from: [x - DEFAULT_NEW_CALLOUT_ARM_PDFPX, y - DEFAULT_NEW_CALLOUT_ARM_PDFPX],
        to: [x, y],
      };
    } else if (addMode === 'scope_box') {
      const r = DEFAULT_NEW_BOX_SIZE_PDFPX / 2;
      nm = {
        type: 'scope_box',
        page: pageNumber,
        risk: 'medium',
        label: 'New scope',
        rect: [x - r, y - r, x + r, y + r],
      };
    } else {
      return;
    }
    setMarkups((prev) => [...prev, nm]);
    setSelectedIdx(markups.length);
    setAddMode(null);
    setDirty(true);
  }, [addMode, pageNumber, markups.length]);

  const onSaveNow = useCallback(async () => {
    if (!sessionId) return;
    try {
      await api.bulkSaveMarkups(sessionId, markups);
      setDirty(false);
      toast.success('Markups saved');
    } catch (err) {
      toast.error(`Save failed: ${err?.response?.data?.error || err.message}`);
    }
  }, [sessionId, markups]);

  const onRender = useCallback(async () => {
    if (!sessionId) return;
    if (markups.length === 0) {
      toast.error('Add at least one markup before rendering.');
      return;
    }
    // Make sure the latest edits are saved before render so the server's
    // version snapshot captures what the user sees.
    setRendering(true);
    try {
      if (dirty) {
        await api.bulkSaveMarkups(sessionId, markups);
        setDirty(false);
      }
      const result = await api.renderSession(sessionId);
      toast.success(`Rendered ${result.markupCount} markup(s) onto page ${result.pageCount > 0 ? '1' : '?'}. Version saved.`);
      // Auto-open the new PDF in a new tab
      if (result.viewUrl) window.open(result.viewUrl, '_blank', 'noopener');
    } catch (err) {
      console.error('[markup-studio] render failed:', err);
      toast.error(`Render failed: ${err?.response?.data?.error || err.message}`);
    } finally {
      setRendering(false);
    }
  }, [sessionId, dirty, markups]);

  if (error) {
    return (
      <div className="p-8 max-w-2xl mx-auto text-center">
        <div className="text-red-700 text-lg font-semibold mb-2">Couldn't load session</div>
        <div className="text-slate-600 text-sm">{error}</div>
        <button
          type="button"
          onClick={() => navigate('/jobotics-agent')}
          className="mt-4 px-3 py-1.5 text-sm rounded bg-slate-800 text-white hover:bg-slate-700"
        >
          Back to agents
        </button>
      </div>
    );
  }

  if (!session) {
    return <div className="p-8 text-slate-500 text-sm">Loading session…</div>;
  }

  const selectedMarkup = selectedIdx >= 0 ? markups[selectedIdx] : null;

  // Route the center panel by source format (mirrors the server's drawingIngest groups):
  //   drawing (pdf/dxf/dwg) → PDF canvas + markup overlay
  //   bim (ifc)             → no 2D sheet; data review happens in the chat
  //   unsupported (rvt/nwd) → proprietary; prompt to export IFC/PDF
  const sourceGroup = (() => {
    const ext = String(session.sourceFilename || '').toLowerCase().split('.').pop();
    if (['pdf', 'dxf', 'dwg'].includes(ext)) return 'drawing';
    if (['ifc', 'ifczip', 'ifcxml'].includes(ext)) return 'bim';
    if (['rvt', 'rfa', 'nwd', 'nwc', 'nwf'].includes(ext)) return 'unsupported';
    return 'drawing';
  })();

  return (
    <div className="flex flex-col h-[calc(100vh-3.5rem)] bg-slate-50">
      {/* Top toolbar */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-slate-200 bg-white">
        <div className="flex items-center gap-3 min-w-0">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="text-slate-500 hover:text-slate-800"
            title="Back"
          >
            <ArrowLeft size={18} />
          </button>
          <div className="truncate">
            <div className="text-sm font-semibold text-slate-800 truncate">
              {session.title || session.sourceFilename}
            </div>
            <div className="text-[11px] text-slate-500 truncate">
              {session.sourceFilename} · {markups.length} markup{markups.length === 1 ? '' : 's'} · page {pageNumber}/{pageCount || '?'}
              {dirty && <span className="ml-2 text-amber-600">● unsaved</span>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => { setDisplayZoom((z) => Math.max(1, +(z - 0.25).toFixed(2))); }}
            disabled={displayZoom <= 1}
            className="p-1 text-slate-500 hover:text-slate-800 disabled:text-slate-300 disabled:cursor-not-allowed"
            title="Zoom out toward fit (floor: 100% / fit-to-window)"
          >
            <ZoomOut size={16} />
          </button>
          <span className="text-xs text-slate-500 w-10 text-center">{Math.round(displayZoom * 100)}%</span>
          <button
            type="button"
            onClick={() => setDisplayZoom((z) => Math.min(6, +(z + 0.25).toFixed(2)))}
            className="p-1 text-slate-500 hover:text-slate-800"
            title="Zoom in (viewport stays the same; shows a smaller portion at higher detail)"
          >
            <ZoomIn size={16} />
          </button>
          <button
            type="button"
            onClick={() => { setDisplayZoom(1); setPanX(0); setPanY(0); }}
            className={`px-1.5 text-xs rounded ${displayZoom === 1 && panX === 0 && panY === 0 ? 'bg-slate-200 text-slate-800' : 'text-slate-500 hover:text-slate-800'}`}
            title="Reset zoom + pan to fit"
          >
            Fit
          </button>
          <span className="w-3" />
          <button
            type="button"
            onClick={onSaveNow}
            disabled={!dirty}
            className="text-xs px-2 py-1 rounded border border-slate-200 hover:bg-slate-50 disabled:opacity-50 flex items-center gap-1"
            title="Save markups"
          >
            <Save size={14} /> Save
          </button>
          <button
            type="button"
            onClick={() => setShowVersions(true)}
            className="text-xs px-2 py-1 rounded border border-slate-200 hover:bg-slate-50 flex items-center gap-1"
            title="Version history"
          >
            <History size={14} /> History
          </button>
          <button
            type="button"
            onClick={onRender}
            disabled={rendering}
            className="text-xs font-semibold px-3 py-1 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1"
            title="Render annotated PDF"
          >
            <RefreshCw size={14} className={rendering ? 'animate-spin' : ''} />
            {rendering ? 'Rendering…' : 'Re-render PDF'}
          </button>
        </div>
      </div>

      {/* Three-panel body */}
      <div className="flex flex-1 min-h-0">
        {/* Left: markup list */}
        <div className="w-72 border-r border-slate-200 bg-white flex flex-col min-h-0">
          <MarkupList
            markups={markups}
            selectedIdx={selectedIdx}
            onSelectMarkup={setSelectedIdx}
            onEditMarkup={onUpdateMarkup}
            onDeleteMarkup={onDeleteMarkup}
            onJumpToPage={setPageNumber}
            onBeginAdd={(t) => setAddMode(t)}
            addMode={addMode}
          />
        </div>

        {/* Center: PDF viewport */}
        <div className="flex-1 flex flex-col min-w-0 min-h-0">
          {/* Page nav */}
          <div className="flex items-center justify-between px-3 py-1.5 border-b border-slate-200 bg-white text-xs text-slate-600">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
                disabled={pageNumber <= 1}
                className="px-2 py-0.5 rounded border border-slate-200 hover:bg-slate-50 disabled:opacity-30"
              >
                ‹ Prev
              </button>
              <span className="font-mono">
                page&nbsp;
                <input
                  type="number"
                  min={1}
                  max={pageCount || 9999}
                  value={pageNumber}
                  onChange={(e) => {
                    const v = Number(e.target.value);
                    if (Number.isInteger(v) && v >= 1 && (!pageCount || v <= pageCount)) setPageNumber(v);
                  }}
                  className="w-12 px-1 border border-slate-200 rounded text-center"
                />
                &nbsp;/ {pageCount || '?'}
              </span>
              <button
                type="button"
                onClick={() => setPageNumber((p) => Math.min(pageCount || p + 1, p + 1))}
                disabled={pageCount > 0 && pageNumber >= pageCount}
                className="px-2 py-0.5 rounded border border-slate-200 hover:bg-slate-50 disabled:opacity-30"
              >
                Next ›
              </button>
            </div>
            {addMode ? (
              <div className="text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded text-[11px]">
                Click on the PDF to drop a <b>{addMode}</b> markup · Esc or Cancel to exit
              </div>
            ) : (
              <div className="text-slate-500 text-[11px] hidden md:block">
                Tip: zoom keeps the viewport size; drag the background to pan · drag a markup to move · middle-click or Space+drag also pans
              </div>
            )}
          </div>

          {/* Fixed-viewport canvas + hand-tool camera pan.
              The viewport stays at fit-to-width SIZE no matter the zoom.
              Mouse-drag moves the CAMERA over the canvas (updating panX/
              panY in canvas-pixel space), not the scrollbars.
              Three ways to engage pan:
                1. Middle-click drag (universal CAD/PDF convention)
                2. Hold Space + left-click drag (Adobe / Figma / Bluebeam)
                3. Left-click drag on the slate background area
              Markup left-click drags still work because addMode short-
              circuits pan, markup handles stopPropagation, and we
              require a 4px movement threshold before pan engages so
              normal clicks aren't hijacked. */}
          <div
            ref={scrollRef}
            className={`flex-1 overflow-hidden p-4 bg-slate-100 flex items-start justify-center ${addMode ? '' : 'cursor-grab active:cursor-grabbing'}`}
            onMouseDown={(e) => {
              if (addMode) return;
              // Accept left-click anywhere (including on the canvas itself
              // — markup body-drag + handle-drag already stopPropagation,
              // so they don't bubble up here). Middle-click and Space+
              // left also accepted as the traditional hand-tool gestures.
              if (e.button !== 0 && e.button !== 1) return;
              e.preventDefault();
              const startX = e.clientX;
              const startY = e.clientY;
              const startPanX = panX;
              const startPanY = panY;
              let activated = false;
              const PAN_THRESHOLD = 4; // px before pan engages — keeps clicks intact
              const onMove = (ev) => {
                const dx = ev.clientX - startX;
                const dy = ev.clientY - startY;
                if (!activated && Math.hypot(dx, dy) < PAN_THRESHOLD) return;
                activated = true;
                // Mouse drags content along with the cursor. In camera
                // terms: cursor moves right → camera moves LEFT by
                // dx/displayZoom (canvas pixel units, not screen px).
                setPanX(startPanX - dx / displayZoom);
                setPanY(startPanY - dy / displayZoom);
              };
              const onUp = () => {
                window.removeEventListener('mousemove', onMove);
                window.removeEventListener('mouseup', onUp);
              };
              window.addEventListener('mousemove', onMove);
              window.addEventListener('mouseup', onUp);
            }}
          >
            {sourceGroup !== 'drawing' ? (
              <div className="max-w-md text-center px-6 py-10 bg-white border border-slate-200 rounded-lg">
                {sourceGroup === 'bim' ? (
                  <>
                    <div className="text-base font-semibold text-slate-800 mb-2">3D BIM model — data review</div>
                    <div className="text-sm text-slate-600">
                      IFC models have no 2D sheet to display here. Use the <b>AI review chat →</b> to
                      explore the model: spatial tree (project → site → building → storeys), element
                      schedule by type, disciplines present, and property sets.
                    </div>
                    <div className="text-[11px] text-slate-400 mt-3">
                      Try: “Give me an overview of this model” or “List all the pipe segments.”
                    </div>
                  </>
                ) : (
                  <>
                    <div className="text-base font-semibold text-slate-800 mb-2">
                      Can’t display this format in-app
                    </div>
                    <div className="text-sm text-slate-600">
                      Revit (.rvt) and Navisworks (.nwd) are proprietary formats with no open viewer.
                      Export to <b>IFC</b> for a data-driven review, or to a <b>PDF / DWG sheet set</b>{' '}
                      for visual markup, then re-upload. The chat → can still advise.
                    </div>
                  </>
                )}
              </div>
            ) : pdfUrl ? (
              <PdfCanvas
                pdfUrl={pdfUrl}
                pageNumber={pageNumber}
                scale={scale}
                fitToWidth={fitToWidth}
                containerRef={scrollRef}
                displayZoom={displayZoom}
                panX={panX}
                panY={panY}
                onReady={onPdfReady}
                onClickPdfCoords={addMode ? onClickPdfCoords : undefined}
              >
                <MarkupOverlay
                  markups={markups}
                  pageNumber={pageNumber}
                  selectedIdx={selectedIdx}
                  onSelectMarkup={setSelectedIdx}
                  onUpdateMarkup={onUpdateMarkup}
                />
              </PdfCanvas>
            ) : (
              <div className="text-sm text-slate-500">No PDF URL.</div>
            )}
          </div>

          {/* Thumbnails strip — disabled for huge drawing sets since each
              thumbnail would trigger its own per-page extraction. Page nav
              via the Prev/Next + page-jump input in the header still works.
              TODO: bring back as a lazy on-demand strip (only render
              thumbnails for the currently-visible window). */}
          {pdfUrl && pageCount > 1 && pageCount <= 20 && (
            <Thumbnails
              pdfUrl={api.sourcePdfUrl(sessionId)}
              pageCount={pageCount}
              currentPage={pageNumber}
              onPick={setPageNumber}
              markups={markups}
            />
          )}
        </div>

        {/* Right: chat */}
        <div className="w-80 border-l border-slate-200 bg-white flex flex-col min-h-0">
          <MarkupChat
            sessionId={sessionId}
            markupIdx={selectedIdx >= 0 ? selectedIdx : null}
            markup={selectedMarkup}
          />
        </div>
      </div>

      <VersionHistory sessionId={sessionId} open={showVersions} onClose={() => setShowVersions(false)} />
    </div>
  );
}
